import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchDuplicates, setGroupDismissed } from '../recordsApi';
import { useAsync } from '../hooks';

const formatPrice = (value) => {
  const number = Number(value);
  return value != null && value !== '' && !Number.isNaN(number) ? `€${number.toLocaleString()}` : value;
};

export default function DuplicatesList() {
  const [showDismissed, setShowDismissed] = useState(false);
  const [reload, setReload] = useState(0);
  const [search, setSearch] = useState('');
  const [actionError, setActionError] = useState(null);
  const { data, loading, error } = useAsync(() => fetchDuplicates(showDismissed), [showDismissed, reload]);

  const groups = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!q) return data.groups;
    return data.groups.filter(
      (g) => g.eircode.toLowerCase().includes(q.replace(/ /g, '')) ||
        g.listings.some((l) => [l.DaftId__c, l.Address__c, l.Broker__c].some((v) => String(v ?? '').toLowerCase().includes(q)))
    );
  }, [data, search]);

  const toggleDismissed = async (group) => {
    setActionError(null);
    try {
      await setGroupDismissed(group.eircode, !showDismissed);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const listingCount = groups.reduce((n, g) => n + g.count, 0);

  return (
    <div className="rec rec-page rec-wide">
      <Link to="/listings" className="rec-btn rec-back">
        <span aria-hidden="true">←</span> Back to listings
      </Link>

      <div className="rec-header rec-header-center">
        <h1>{showDismissed ? 'Dismissed duplicates' : 'Possible duplicates'}</h1>
        <div className="rec-header-right">
          {data && <span className="rec-muted rec-sm">{groups.length} Eircodes · {listingCount} listings</span>}
          <button type="button" className="rec-btn" onClick={() => setShowDismissed(!showDismissed)}>
            {showDismissed ? 'Show possible duplicates' : `Show dismissed${data && data.dismissed_count ? ` (${data.dismissed_count})` : ''}`}
          </button>
        </div>
      </div>

      <p className="rec-muted rec-sm rec-intro">
        Listings that share an Eircode. Merge them into one record, or mark the Eircode as not a duplicate (for example flats in one block).
      </p>

      <input type="search" className="rec-search" placeholder="Search Eircode, Daft ID, address, broker…" aria-label="Search duplicates" value={search} onChange={(e) => setSearch(e.target.value)} />

      {loading && <p className="rec-muted rec-sm">Loading…</p>}
      {error && <p className="rec-error rec-sm">Failed to load duplicates: {error.message}</p>}
      {actionError && <p className="rec-error rec-sm rec-banner">{actionError}</p>}

      {data && (
        <div className="rec-table-wrap">
          <table className="rec-table">
            <thead>
              <tr>
                <th>Daft ID</th>
                <th>Address</th>
                <th>Price</th>
                <th>Beds</th>
                <th>Baths</th>
                <th>BER</th>
                <th>Broker</th>
                <th>Listed</th>
              </tr>
            </thead>
            {groups.map((g) => (
              <tbody key={g.eircode} className="rec-group">
                <tr className="rec-group-head">
                  <td colSpan={8}>
                    <div className="rec-group-bar">
                      <span><strong>{g.eircode}</strong> <span className="rec-muted">· {g.count} listings</span></span>
                      <span className="rec-group-actions">
                        {!showDismissed && (
                          <Link to={`/duplicates/${encodeURIComponent(g.eircode)}/merge`} className="rec-btn-primary">Merge</Link>
                        )}
                        <button type="button" className="rec-btn" onClick={() => toggleDismissed(g)}>
                          {showDismissed ? 'Restore' : 'Not duplicates'}
                        </button>
                      </span>
                    </div>
                  </td>
                </tr>
                {g.listings.map((l) => (
                  <tr key={l.DaftId__c}>
                    <td><Link to={`/listings/${encodeURIComponent(l.DaftId__c)}`} className="rec-row-link">{l.DaftId__c}</Link></td>
                    <td className="rec-cell-wrap">{l.Address__c}</td>
                    <td>{formatPrice(l.Asking_Price__c)}</td>
                    <td>{l.Beds__c}</td>
                    <td>{l.Baths__c}</td>
                    <td>{l.BER_Rating__c}</td>
                    <td className="rec-cell-wrap">{l.Broker__c}</td>
                    <td className="rec-muted">{l.Date_Listed__c?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            ))}
            {groups.length === 0 && (
              <tbody><tr><td colSpan={8} className="rec-muted rec-empty">{showDismissed ? 'Nothing has been dismissed.' : 'No possible duplicates.'}</td></tr></tbody>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
